import {
	Suspense,
	useCallback,
	useEffect,
	useRef,
	useState,
	type ReactNode
} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
	getEntityDetailComponent,
	ENTITY_DISPLAY_NAMES,
	ENTITY_ID_PROP_NAMES,
	ENTITY_RESOURCE_PATHS
} from '@/contexts/EntityModalContext';
import {
	getActiveNetworkActivityCount,
	subscribeNetworkActivity
} from '@/lib/network-activity';

declare global {
	interface Window {
		__vectroSplashEntityPending?: () => void;
		__vectroSplashEntityReady?: () => void;
	}
}

interface DetailMountSignalProps {
	onMount: () => void;
	children: ReactNode;
}

const DetailMountSignal = ({ onMount, children }: DetailMountSignalProps) => {
	useEffect(() => {
		onMount();
	}, [onMount]);

	return <>{children}</>;
};

export function DetachedEntityView() {
	const { entityType, entityId } = useParams<{
		entityType: string;
		entityId: string;
	}>();
	const navigate = useNavigate();
	const [isDetailMounted, setIsDetailMounted] = useState(false);
	const [activeNetworkRequests, setActiveNetworkRequests] = useState(
		getActiveNetworkActivityCount()
	);
	const hasSeenNetworkActivityRef = useRef(false);
	const hasSignaledReadyRef = useRef(false);
	const DetailComponent = entityType
		? getEntityDetailComponent(entityType)
		: undefined;

	const signalEntityReady = useCallback(() => {
		if (hasSignaledReadyRef.current) {
			return;
		}

		hasSignaledReadyRef.current = true;
		window.__vectroSplashEntityReady?.();
	}, []);

	const handleDetailMounted = useCallback(() => {
		hasSeenNetworkActivityRef.current = activeNetworkRequests > 0;
		setIsDetailMounted(true);
	}, [activeNetworkRequests]);

	// Set document title based on entity type
	useEffect(() => {
		const displayName = entityType
			? ENTITY_DISPLAY_NAMES[entityType] || entityType
			: 'Details';
		document.title = `${displayName} Details - Vectro`;

		return () => {
			document.title = 'Vectro';
		};
	}, [entityType]);

	// Detached views should hold splash until entity data is ready.
	useEffect(() => {
		window.__vectroSplashEntityPending?.();
	}, []);

	useEffect(() => {
		const unsubscribe = subscribeNetworkActivity(count => {
			setActiveNetworkRequests(count);
		});

		return unsubscribe;
	}, []);

	useEffect(() => {
		if (!isDetailMounted) {
			return;
		}

		if (activeNetworkRequests > 0) {
			hasSeenNetworkActivityRef.current = true;
		}
	}, [activeNetworkRequests, isDetailMounted]);

	// If route params/component are invalid, release splash immediately.
	useEffect(() => {
		if (!entityType || !entityId || !DetailComponent) {
			signalEntityReady();
		}
	}, [DetailComponent, entityId, entityType, signalEntityReady]);

	// Wait until detail mounts and network settles before dismissing splash.
	// If no network starts, allow a small settle window.
	useEffect(() => {
		if (!isDetailMounted) {
			return;
		}

		if (hasSeenNetworkActivityRef.current) {
			if (activeNetworkRequests === 0) {
				signalEntityReady();
			}
			return;
		}

		const settleTimer = window.setTimeout(() => {
			if (activeNetworkRequests === 0) {
				signalEntityReady();
			}
		}, 450);

		return () => {
			window.clearTimeout(settleTimer);
		};
	}, [activeNetworkRequests, isDetailMounted, signalEntityReady]);

	if (!entityType || !entityId) {
		return (
			<div className="flex items-center justify-center min-h-screen text-muted-foreground">
				Invalid entity parameters.
			</div>
		);
	}

	if (!DetailComponent) {
		return (
			<div className="flex items-center justify-center min-h-screen text-muted-foreground">
				No detail component found for entity type: {entityType}
			</div>
		);
	}

	const idPropName =
		ENTITY_ID_PROP_NAMES[entityType] || `${entityType}Id`;
	const resourcePath = ENTITY_RESOURCE_PATHS[entityType];

	const handleEdit = (id: string) => {
		if (resourcePath) {
			navigate(`${resourcePath}/edit/${id}`);
		}
	};

	const handleClone = (id: string) => {
		if (resourcePath) {
			navigate(`${resourcePath}/clone/${id}`);
		}
	};

	const handleDelete = () => {
		// After deletion, close the detached window
		window.close();
	};

	const props: Record<string, unknown> = {
		[idPropName]: entityId,
		showHeader: true,
		onEdit: handleEdit,
		onClone: handleClone,
		onDelete: handleDelete
	};

	return (
		<div className="min-h-screen bg-white dark:bg-slate-900">
			<div className="max-w-4xl mx-auto p-6">
				<Suspense
					fallback={
						<div className="flex items-center justify-center py-12">
							<Loader2 className="w-5 h-5 animate-spin text-muted-foreground mr-2" />
							<span className="text-sm text-muted-foreground">
								Loading details...
							</span>
						</div>
					}
				>
					<DetailMountSignal onMount={handleDetailMounted}>
						<DetailComponent {...props} />
					</DetailMountSignal>
				</Suspense>
			</div>
		</div>
	);
}
